import { createContext, PropsWithChildren, useContext, useState } from 'react'

import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import { IApplicationState } from '@store'
import {
  activate,
  downloadInstagramHandles,
  pause,
  selectCampaignLink,
  selectCampaignTitle,
  selectCopyCampaignLink,
  selectInternalEditLink,
  selectShowDownloadInstagramHandlesButton,
} from '@store/campaigns'
import CampaignCardModalActivate from '../Card/Modal/Activate/Activate'
import CampaignCardModalCopyCampaign from '../Card/Modal/CopyCampaign/CopyCampaign'
import CampaignCardModalPause from '../Card/Modal/Pause/Pause'

interface ICampaignModalState {
  activate: boolean
  pause: boolean
  copy: boolean
}

const INITIAL_CAMPAIGN_MODAL_STATE: ICampaignModalState = {
  activate: false,
  copy: false,
  pause: false,
}

interface IProps {
  campaignId: number
}

interface ICampaignMenuActionsContextProps extends IProps {
  onOpenConfirmModal: VoidFunction
  onOpenPauseModal: VoidFunction
  openCampaignModal: (modal: keyof ICampaignModalState) => void
  onDownloadInstagramHandles: VoidFunction
  hasActivateLink: boolean
  hasCopyCampaignLink: boolean
  hasPauseLink: boolean
  hasDownloadInstagramHandles: boolean
  hasAnyAction: boolean
  editLink: string
}

const CampaignMenuActionsContext = createContext({} as ICampaignMenuActionsContextProps)

const useCampaignMenuActions = () => useContext(CampaignMenuActionsContext)

const CampaignMenuActionsProvider = ({ campaignId, children }: PropsWithChildren<IProps>) => {
  const dispatch = useDispatch()
  const {
    hasActivateLink,
    hasPauseLink,
    campaignTitle,
    editLink,
    hasAnyAction,
    hasDownloadInstagramHandles,
    hasCopyCampaignLink,
  } = useSelector((state: IApplicationState) => {
    const hasActivateLink = Boolean(selectCampaignLink(state, campaignId, 'activate'))
    const hasPauseLink = Boolean(selectCampaignLink(state, campaignId, 'pause'))
    const hasDownloadInstagramHandles = selectShowDownloadInstagramHandlesButton(state, campaignId)
    const hasCopyCampaignLink = Boolean(selectCopyCampaignLink(state, campaignId))
    return {
      hasActivateLink,
      hasPauseLink,
      hasDownloadInstagramHandles,
      hasAnyAction: [
        hasActivateLink,
        hasPauseLink,
        hasDownloadInstagramHandles,
        hasCopyCampaignLink,
      ].some(Boolean),
      hasCopyCampaignLink: Boolean(selectCopyCampaignLink(state, campaignId)),
      editLink: selectInternalEditLink(state, campaignId) as string,
      campaignTitle: selectCampaignTitle(state, campaignId),
    }
  }, shallowEqual)
  const [modalState, setModalState] = useState(INITIAL_CAMPAIGN_MODAL_STATE)

  const onActivate = () => {
    dispatch(activate(campaignId))
    onCancelModal()
  }

  const openCampaignModal = (modalName: keyof ICampaignModalState) => {
    setModalState((state) => ({ ...state, [modalName]: true }))
  }

  const onCancelModal = () => {
    setModalState(INITIAL_CAMPAIGN_MODAL_STATE)
  }

  const onDownloadInstagramHandles = () => {
    dispatch(downloadInstagramHandles(campaignId, `${campaignTitle} ${Date.now()}`))
  }

  const onOpenConfirmModal = () => {
    openCampaignModal('activate')
  }

  const onOpenPauseModal = () => {
    openCampaignModal('pause')
  }

  const onPause = () => {
    dispatch(pause(campaignId))
    onCancelModal()
  }

  return (
    <CampaignMenuActionsContext.Provider
      value={{
        campaignId,
        onOpenConfirmModal,
        onOpenPauseModal,
        onDownloadInstagramHandles,
        openCampaignModal,
        hasActivateLink,
        hasCopyCampaignLink,
        hasPauseLink,
        hasDownloadInstagramHandles,
        hasAnyAction,
        editLink,
      }}
    >
      <CampaignCardModalActivate
        campaignId={campaignId}
        onConfirm={onActivate}
        onCancel={onCancelModal}
        isOpen={modalState.activate}
      />
      <CampaignCardModalPause
        onConfirm={onPause}
        onCancel={onCancelModal}
        isOpen={modalState.pause}
      />
      <CampaignCardModalCopyCampaign
        campaignId={campaignId}
        onCancel={onCancelModal}
        isOpen={modalState.copy}
      />
      {children}
    </CampaignMenuActionsContext.Provider>
  )
}

export { CampaignMenuActionsContext, CampaignMenuActionsProvider, useCampaignMenuActions }
export default CampaignMenuActionsProvider
